import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Hero } from "@/components/home/Hero";
import { Categories } from "@/components/home/Categories";
import { Brands } from "@/components/home/Brands";
import { ProductGrid } from "@/components/products/ProductGrid";
import { About } from "@/components/home/About";
import { News } from "@/components/home/News";
import { GoogleReviews } from "@/components/home/GoogleReviews";
import { BusterPunch } from "@/components/home/BusterPunch";
import { Achievements } from "@/components/home/Achievements";
import { SeoHead } from "@/components/SeoHead";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNews } from "@/hooks/useNews";

const Index = () => {
  const { language } = useLanguage();
  const { news, loading: newsLoading } = useNews();
  
  const seoData = {
    en: {
      title: "HAUS | Premium Interior Design Products",
      description: "Premium door handles, switches, lighting and bathroom fixtures from Buster + Punch, Deante, Victoria + Albert, Tres and more.",
    },
    lv: {
      title: "HAUS | Premium interjera dizaina produkti",
      description: "Premium durvju rokturi, slēdži, apgaismojums un vannas istabu aprīkojums no Buster + Punch, Deante, Victoria + Albert, Tres.",
    },
    ru: {
      title: "HAUS | Премиальные товары для интерьера",
      description: "Премиальные дверные ручки, выключатели, освещение и сантехника от Buster + Punch, Deante, Victoria + Albert, Tres.",
    },
  };

  const seo = seoData[language] || seoData.en;

  const hasNews = !newsLoading && news.length > 0;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://www.haus.lv/#organization",
        "name": "HAUS",
        "url": "https://www.haus.lv",
        "logo": "https://www.haus.lv/favicon.png",
        "description": "Premium interior design products — door handles, switches, lighting, bathroom fixtures and architectural hardware from Buster + Punch, Deante, Victoria + Albert, Tres, Noken, TECE.",
        "areaServed": ["LV", "EE", "LT", "EU"],
        "knowsLanguage": ["en", "lv", "ru"],
        "sameAs": []
      },
      {
        "@type": "WebSite",
        "@id": "https://www.haus.lv/#website",
        "url": "https://www.haus.lv",
        "name": "HAUS",
        "publisher": { "@id": "https://www.haus.lv/#organization" },
        "inLanguage": ["en", "lv", "ru"],
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://www.haus.lv/en/shop?search={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "LocalBusiness",
        "@id": "https://www.haus.lv/#localbusiness",
        "name": "HAUS",
        "image": "https://www.haus.lv/favicon.png",
        "url": "https://www.haus.lv",
        "telephone": "+371 6737 6780",
        "email": "haus@royal-bathrooms.com",
        "priceRange": "€€€",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Cēsu iela 31 k-3",
          "addressLocality": "Rīga",
          "postalCode": "LV-1012",
          "addressCountry": "LV"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 56.966869,
          "longitude": 24.141348
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
            "opens": "10:00",
            "closes": "19:00"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": "Saturday",
            "opens": "11:00",
            "closes": "16:00"
          }
        ],
        "sameAs": [
          "https://www.instagram.com/royalbathroomsriga/",
          "https://www.facebook.com/RoyalBathrooms/"
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen">
      <SeoHead title={seo.title} description={seo.description} jsonLd={jsonLd} />
      <Header />
      <main>
        {/* Show news as hero when available, otherwise standard hero */}
        {hasNews ? (
          <News news={news} heroMode />
        ) : (
          <Hero />
        )}
        
        {/* Elegant divider section */}
        <section className="bg-charcoal py-4 lg:py-6">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center gap-6">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
              <span className="text-gold font-display text-sm tracking-[0.3em] uppercase">
                HAUS
              </span>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
            </div>
          </div>
        </section>
        
        <Categories />
        <Brands />
        <ProductGrid />
        <BusterPunch />
        <Achievements />
        <GoogleReviews />
        {/* Also show news as regular section below if present */}
        <About />
      </main>
      <Footer />
    </div>
  );
};

export default Index;
